import { useState } from 'react';
import { commandCenterApi } from '../../utils/api';
import { toast } from '../../components/Toast';
import { fmt } from './statsParts';

// החלת דוח ח.פ. — the production half of server/scripts/backfillSupplierIds.js.
// The script reads past documents on a COPY of the database and writes a
// report.json; the owner reviews it, then uploads it here and the server
// writes it through its own connection. Choosing the file only PREVIEWS (the
// same writes, rolled back); nothing is stored until "החל" is clicked. Rerun
// is harmless — only empty values are filled (lib/supplierIdBackfill.js).

function KeptDifferent({ rows }) {
  if (!rows.length) return null;
  return (
    <>
      <p className="cc-muted" style={{ margin: '12px 0 6px' }}>
        {fmt(rows.length)} ספקים כבר עם ח.פ. אחר מזה שנקרא — לא ישתנו. כדאי לבדוק:
      </p>
      <div className="cc-matrix-scroll">
        <table className="cc-list">
          <thead><tr><th>מסעדה</th><th>ספק</th><th>ח.פ. במערכת</th><th>ח.פ. שנקרא</th></tr></thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.supplier_id}>
                <td>{r.restaurant_name}</td>
                <td style={{ fontWeight: 600 }}>{r.supplier_name}</td>
                <td className="cc-num">{r.current}</td>
                <td className="cc-num">{r.read}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function Outcome({ data }) {
  const r = data.result;
  const got = data.applied ? 'קיבלו' : 'יקבלו';
  return (
    <div style={{ marginTop: 10 }}>
      <ul style={{ margin: 0, paddingInlineStart: 18, lineHeight: 1.8 }}>
        <li><b>{fmt(r.suppliers)}</b> ספקים {got} ח.פ. (אותו מספר בשתי קריאות)</li>
        <li><b>{fmt(r.documents)}</b> מסמכים {got} את שם הספק והח.פ. כפי שמודפסים</li>
        <li className="cc-muted">
          {fmt(r.suppliersUnconfirmed)} ספקים עם קריאה אחת בלבד — {data.applied ? 'לא מולאו' : 'לא ימולאו'}
          · {fmt(r.suppliersUnchanged)} כבר עם אותו ח.פ.
          · {fmt(r.skippedDocuments)} מסמכים דולגו (כבר נקראו, או הועברו לספק אחר)
        </li>
      </ul>
      <KeptDifferent rows={r.suppliersKeptDifferent} />
    </div>
  );
}

export default function BackfillReportSection({ onApplied }) {
  const [file, setFile] = useState(null);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const send = async (f, apply) => {
    setBusy(true);
    try {
      const r = await commandCenterApi.supplierIdBackfill(f, apply);
      setData(r.data);
      if (apply) {
        toast(`הדוח הוחל: ${fmt(r.data.result.suppliers)} ספקים, ${fmt(r.data.result.documents)} מסמכים`);
        onApplied?.();
      }
    } catch (err) {
      setData(null);
      toast(err.response?.data?.error || 'הפעולה נכשלה — אפשר לנסות שוב', 'error');
    } finally {
      setBusy(false);
    }
  };

  const choose = (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';   // choosing the same file again re-previews
    if (!f) return;
    setFile(f);
    send(f, false);
  };

  return (
    <section className="cc-tabpanel-section">
      <div className="cc-card-head" style={{ flexWrap: 'wrap' }}>
        <h2 className="cc-card-title">החלת דוח ח.פ.</h2>
        {data && <span className={`cc-pill ${data.applied ? 'ok' : 'warn'}`}>{data.applied ? 'הוחל' : 'תצוגה מקדימה — עוד לא נשמר'}</span>}
        <span className="cc-spacer" />
        <label className={`btn btn-sm btn-outline${busy ? ' disabled' : ''}`} style={{ cursor: busy ? 'default' : 'pointer' }}>
          {file ? 'דוח אחר…' : 'בחירת report.json…'}
          <input type="file" accept=".json,application/json" hidden disabled={busy} onChange={choose}
            aria-label="בחירת קובץ report.json" />
        </label>
      </div>
      <p className="cc-muted" style={{ margin: 0, fontSize: '0.83rem' }}>
        הדוח שיצר סקריפט ה-backfill (<bdi>server/backfill-reports/…/report.json</bdi>). בחירת הקובץ מציגה מה ישתנה — שום דבר לא נשמר עד הלחיצה על &quot;החל&quot;.
        ממלא רק שדות ריקים; ספקי &quot;החברה המרכזית&quot; לא נוגעים.
      </p>
      {busy && !data && <p className="cc-muted">בודק את הדוח…</p>}
      {data && (
        <>
          {file && <p className="cc-muted" style={{ margin: '8px 0 0', fontSize: '0.79rem' }}><bdi>{file.name}</bdi> · {fmt(data.reads)} קריאות</p>}
          <Outcome data={data} />
          {!data.applied && (
            <button type="button" className="btn btn-success" style={{ marginTop: 12 }}
              disabled={busy || (!data.result.suppliers && !data.result.documents)} onClick={() => send(file, true)}>
              {busy ? 'מחיל…' : 'החל על המערכת'}
            </button>
          )}
        </>
      )}
    </section>
  );
}
